import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Users, Shield, Wifi } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { getCountryDisplayName } from "@/utils/countryTranslations";

interface Company {
  id: string;
  name: string;
  sector: string;
  employee_count: number;
}

interface EmployeeReportData {
  id: string;
  firstName: string;
  lastName: string;
  workLocation: string;
  totalRemoteDays: number;
  currentStartDate: string | null;
  currentEndDate: string | null;
  insuranceActive: boolean;
  vpnActive: boolean;
}

interface EmployeeReportingTableProps {
  company: Company | null;
}

const EmployeeReportingTable = ({ company }: EmployeeReportingTableProps) => {
  const { t, i18n } = useTranslation();
  const [employees, setEmployees] = useState<EmployeeReportData[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (company) {
      fetchEmployeeData();
    }
  }, [company]);

  const fetchEmployeeData = async () => {
    if (!company) return;

    try {
      // Fetch employees with their remote work requests
      const { data: employeesData } = await supabase
        .from('employees')
        .select(`
          id,
          first_name,
          last_name,
          current_country,
          residence_country,
          position
        `)
        .eq('company_id', company.id);

      if (!employeesData) return;

      // For each employee, calculate their remote work data
      const employeeReports = await Promise.all(
        employeesData.map(async (emp) => {
          // Get all approved remote work requests for this employee
          // Use the secure RPC function
          const { data: allRequests } = await supabase
            .rpc('get_company_remote_work_requests');

          const requests = (allRequests || [])
            .filter(req => req.employee_id === emp.id && req.status === 'approved')
            .sort((a, b) => new Date(b.start_date).getTime() - new Date(a.start_date).getTime());

          // Calculate total remote days
          let totalDays = 0;
          let currentStartDate = null;
          let currentEndDate = null;
          let hasInsurance = false;
          let hasVpn = false;

          const today = new Date();
          const todayStr = today.toISOString().split('T')[0];

          if (requests) {
            requests.forEach((req) => {
              const startDate = new Date(req.start_date);
              const endDate = new Date(req.end_date);
              
              // Calculate days for completed or ongoing requests
              const effectiveEndDate = endDate > today ? today : endDate;
              if (effectiveEndDate >= startDate) {
                const timeDiff = effectiveEndDate.getTime() - startDate.getTime();
                const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1;
                totalDays += daysDiff;
              }

              // Check if currently remote
              if (req.start_date <= todayStr && req.end_date >= todayStr) {
                currentStartDate = req.start_date;
                currentEndDate = req.end_date;
                hasInsurance = req.insurance_active || false;
                hasVpn = req.vpn_active || false;
              }
            });
          }

          return {
            id: emp.id,
            firstName: emp.first_name || 'N/A',
            lastName: emp.last_name || 'N/A',
            workLocation: emp.current_country || emp.residence_country || null,
            totalRemoteDays: totalDays,
            currentStartDate,
            currentEndDate,
            insuranceActive: hasInsurance,
            vpnActive: hasVpn
          };
        })
      );

      setEmployees(employeeReports);
    } catch (error) {
      console.error('Error fetching employee data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getDateLocale = () => {
    return i18n.language === 'fr' ? 'fr-FR' : i18n.language === 'de' ? 'de-DE' : i18n.language === 'es' ? 'es-ES' : 'en-US';
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="h-32 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          {t("dashboard.reports.employeeTracking.title", "Detailed Employee Tracking")}
        </CardTitle>
        <CardDescription>
          {t("dashboard.reports.employeeTracking.subtitle", "Complete information on each employee's remote work activity")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {employees.length === 0 ? (
          <div className="text-center py-8">
            <Users className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground">{t("dashboard.reports.employeeTracking.noEmployees", "No employees found")}</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("dashboard.reports.employeeTracking.columns.employee", "Employee")}</TableHead>
                  <TableHead>{t("dashboard.reports.employeeTracking.columns.workLocation", "Work Location")}</TableHead>
                  <TableHead className="text-center">{t("dashboard.reports.employeeTracking.columns.remoteDays", "Remote Days")}</TableHead>
                  <TableHead>{t("dashboard.reports.employeeTracking.columns.currentPeriod", "Current Period")}</TableHead>
                  <TableHead className="text-center">{t("dashboard.reports.employeeTracking.columns.insurance", "Insurance")}</TableHead>
                  <TableHead className="text-center">{t("dashboard.reports.employeeTracking.columns.vpn", "VPN")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {employees.map((employee) => (
                  <TableRow key={employee.id}>
                    <TableCell>
                      <div className="font-medium">
                        {employee.firstName} {employee.lastName}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-muted-foreground">
                        {employee.workLocation 
                          ? getCountryDisplayName(employee.workLocation, i18n.language)
                          : t("dashboard.reports.employeeTracking.notSpecified", "Not specified")}
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="secondary" className="font-mono">
                        {employee.totalRemoteDays} {t("dashboard.reports.days", "days")}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {employee.currentStartDate && employee.currentEndDate ? (
                        <div className="text-sm">
                          <div>{t("dashboard.reports.employeeTracking.from", "From")} {new Date(employee.currentStartDate).toLocaleDateString(getDateLocale())}</div>
                          <div>{t("dashboard.reports.employeeTracking.to", "To")} {new Date(employee.currentEndDate).toLocaleDateString(getDateLocale())}</div>
                        </div>
                      ) : (
                        <span className="text-muted-foreground text-sm">{t("dashboard.reports.employeeTracking.notRemote", "Not currently remote")}</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Shield className={`h-4 w-4 ${employee.insuranceActive ? 'text-green-600' : 'text-gray-400'}`} />
                        <span className={`text-sm ${employee.insuranceActive ? 'text-green-600' : 'text-gray-400'}`}>
                          {employee.insuranceActive 
                            ? t("dashboard.reports.employeeTracking.insuranceActive", "Insurance active") 
                            : t("dashboard.reports.employeeTracking.insuranceInactive", "Insurance inactive")}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-center">
                      <div className="flex items-center justify-center gap-1">
                        <Wifi className={`h-4 w-4 ${employee.vpnActive ? 'text-green-600' : 'text-gray-400'}`} />
                        <span className={`text-sm ${employee.vpnActive ? 'text-green-600' : 'text-gray-400'}`}>
                          {employee.vpnActive 
                            ? t("dashboard.reports.employeeTracking.vpnActive", "VPN Active") 
                            : t("dashboard.reports.employeeTracking.vpnInactive", "VPN Inactive")}
                        </span>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default EmployeeReportingTable;